import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/ui/dialog'
import { MaterialIcon } from '@/ui/material-icon'
import styles from './PrerequisitesModal.module.scss'

/** Shape of the prerequisite data returned by the API.
 * Example:
 * ```json
 * {
 *   "instance": "snowflake",
 *   "title": "To connect Snowflake to Akashic, you need the following:",
 *   "details": ["Snowflake", "Account (ex. jbghrdb-qsb87111)", ...],
 *   "important": "IMPORTANT: We only support the official editions of Snowflake."
 * }
 * ```
 */
export type PrerequisiteData = {
    instance?: string
    title?: string
    details?: string[]
    important?: string
}

/**
 * Safely coerce an `unknown` API value into a PrerequisiteData shape.
 * Accepts objects with `instance`, `title`, `details`, and/or `important` keys.
 * Falls back gracefully if the shape doesn't match.
 */
export function parsePrerequisite(raw: unknown): PrerequisiteData | null {
    if (raw == null || typeof raw !== 'object') return null
    const obj = raw as Record<string, unknown>
    const instance = typeof obj.instance === 'string' ? obj.instance : undefined
    const title = typeof obj.title === 'string' ? obj.title : undefined
    const important = typeof obj.important === 'string' ? obj.important : undefined
    let details: string[] | undefined
    if (Array.isArray(obj.details)) {
        details = obj.details.filter((v): v is string => typeof v === 'string')
    }
    if (!title && !details?.length && !important) return null
    return { instance, title, details, important }
}

type PrerequisitesModalProps = {
    open: boolean
    onOpenChange: (open: boolean) => void
    /** Display name of the connector (e.g., "Postgres", "AWS S3"). */
    connectorName?: string
    /** Raw prerequisite data from the API. */
    prerequisite: unknown
}

export function PrerequisitesModal({
    open,
    onOpenChange,
    connectorName,
    prerequisite,
}: PrerequisitesModalProps) {
    const data = parsePrerequisite(prerequisite)

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className={styles.content}>
                <DialogHeader className={styles.header}>
                    <DialogTitle className={styles.title}>
                        Prerequisites
                    </DialogTitle>
                    <DialogDescription className={styles.subtitle}>
                        Ensure the following requirements are in place before adding a{' '}
                        {connectorName ? connectorName : 'source'} to the pipeline.
                    </DialogDescription>
                </DialogHeader>

                {data ? (
                    <div className={styles.body}>
                        {data.title && (
                            <p className={styles.intro}>{data.title}</p>
                        )}

                        {data.details && data.details.length > 0 && (
                            <ul className={styles.list}>
                                {data.details.map((item, idx) => (
                                    <li key={idx} className={styles.listItem}>
                                        <span className={styles.bullet}>
                                            <MaterialIcon name="circle" size={6} />
                                        </span>
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        )}

                        {data.important && (
                            <div className={styles.important}>
                                <span className={styles.importantLabel}>
                                    {data.important.startsWith('IMPORTANT:')
                                        ? 'IMPORTANT:'
                                        : data.important.split(':')[0] + ':'}
                                </span>
                                <span>
                                    {data.important.startsWith('IMPORTANT:')
                                        ? data.important.slice('IMPORTANT:'.length).trimStart()
                                        : data.important.split(':').slice(1).join(':').trimStart()}
                                </span>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className={styles.body}>
                        <p className={styles.noData}>
                            No prerequisite information is available for this connector.
                        </p>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    )
}
